/**
 * SATCHEL API Client
 *
 * Client for the rgb-satchel HTTP API: wallet management, Bitcoin and RGB
 * operations. The API requires a token on every route but health. The client
 * never holds it: in the desktop app the main process makes the call, and on
 * the web a same-origin proxy adds it.
 */

import { SATCHEL_API_CONFIG } from "../config";
import { getElectronAPI } from "../adapters/electron";
import {
  type ApiReply,
  type ApiRequest,
  type Send,
  fetchSender,
  replyErrorMessage,
  transportError,
} from "./apiTransport";

export interface WalletCreateResponse {
  name: string;
  network: string;
  mnemonic: string;
  first_address: string;
  f1r3fly_pubkey: string;
}

export interface WalletImportResponse {
  name: string;
  network: string;
  first_address: string;
  f1r3fly_pubkey: string;
}

export interface WalletListItem {
  name: string;
  network: string;
  created_at: string;
}

export type WalletListResponse = WalletListItem[];

export interface WalletDeleteResponse {
  name: string;
  deleted: boolean;
}

export interface WalletPubkeyResponse {
  pubkey: string;
}

export interface SyncResponse {
  height: number;
  new_txs: number;
}

export interface BitcoinBalance {
  confirmed: number;
  unconfirmed: number;
  total: number;
  utxo_count: number;
  available_count: number;
  rgb_occupied_count: number;
}

export interface WalletAddress {
  index: number;
  address: string;
  is_used: boolean;
}

/** RGB asset info attached to a UTXO */
export interface RgbSealInfo {
  contract_id: string;
  ticker: string;
  amount: number | null;
}

export type UtxoStatus = "available" | "rgb_occupied" | "unconfirmed";

export interface UTXO {
  /** "txid:vout" */
  outpoint: string;
  txid: string;
  vout: number;
  amount_sats: number;
  amount_btc: number;
  confirmations: number;
  status: UtxoStatus;
  rgb_assets: RgbSealInfo[];
  /** True if on the colored (External) keychain and eligible as an RGB seal */
  colorable: boolean;
}

export interface ListUtxosResponse {
  wallet: string;
  total_utxos: number;
  available_count: number;
  rgb_occupied_count: number;
  utxos: UTXO[];
}

export interface CreateColoredUtxosResponse {
  txid: string;
  created: number;
  /** Created outpoints as "txid:vout" strings */
  utxos: string[];
  fee_sats: number;
  fee_rate: number;
}

export interface SendBitcoinResponse {
  txid: string;
  to_address: string;
  amount_sats: number;
  fee_sats: number;
}

/** An RGB contract with its issuance details. */
export interface ContractInfo {
  contract_id: string;
  ticker: string;
  name: string;
  supply: number;
  precision: number;
  genesis_seal: string;
  registry_uri: string;
}

export type IssueAssetResponse = ContractInfo;

export interface RGBAsset {
  contract_id: string;
  ticker: string;
  name: string;
  registry_uri: string;
}

export interface UtxoBalance {
  outpoint: string;
  amount: number;
}

export interface RGBBalance {
  contract_id: string;
  ticker: string;
  name: string;
  total: number;
  precision: number;
  utxo_balances: UtxoBalance[];
}

export interface InvoiceResponse {
  invoice: string;
  address: string;
  contract_id: string;
  amount: number;
}

export interface ParsedInvoice {
  contract_id: string;
  amount: number | null;
  beneficiary: string;
  address?: string;
}

export interface SendTransferResponse {
  bitcoin_txid: string;
  /** The consignment document the recipient must accept. */
  consignment: unknown;
  amount_sent: number;
  change_amount: number;
}

/** A consignment document as a file the user can save and hand to the recipient. */
export function consignmentBlob(consignment: unknown): Blob {
  return new Blob([JSON.stringify(consignment)], { type: "application/json" });
}

export interface AcceptConsignmentResponse {
  contract_id: string;
  ticker: string;
  name: string;
  seals_imported: number;
}

export type ClaimStatus = "pending" | "claimed" | "failed";

export interface Claim {
  id: number;
  witness_id: string;
  contract_id: string;
  status: ClaimStatus;
  error_message: string | null;
  /** Unix seconds */
  created_at: number;
  claimed_at: number | null;
  /** Outpoint of the witness transaction that carries the transfer */
  actual_txid: string;
  actual_vout: number;
}

export interface ListClaimsResponse {
  claims: Claim[];
  pending_count: number;
  claimed_count: number;
  failed_count: number;
}

/**
 * Where an outgoing transfer stands. `waiting_contract`: the contract call is
 * recorded but its outcome is not known yet. `waiting_counterparty`: prepared,
 * the network has not been seen to have its transaction. A transfer is `failed`
 * only when its tokens are known not to have moved.
 */
export type TransferStatus =
  | "waiting_contract"
  | "waiting_counterparty"
  | "waiting_confirmations"
  | "settled"
  | "failed";

export interface Transfer {
  idx: number;
  contract_id: string;
  amount: number;
  recipient_pubkey: string;
  /** Witness transaction, known once the transfer has been prepared */
  txid: string | null;
  status: TransferStatus;
  /** Unix seconds */
  created_at: number;
  updated_at: number;
}

export interface ListTransfersResponse {
  transfers: Transfer[];
  total: number;
  pending_count: number;
  settled_count: number;
  failed_count: number;
}

export interface RefreshResponse {
  updated_count: number;
  updated: { idx: number; status: TransferStatus }[];
}

/** The wallet's F1r3fly vault, which pays for its contract deploys. */
export interface VaultResponse {
  address: string;
  /** Base units; 100,000,000 make one token. */
  balance_dust: number;
}

export interface RecoveryPhraseResponse {
  mnemonic: string;
}

export interface F1r3nodeEndpoint {
  host: string;
  grpc_port: number;
  http_port: number;
}

/** What the shard's validator reports about itself. */
export interface ShardStatus {
  ready: boolean;
  shard_id: string;
  finalized_height: number;
  peers: number;
  node_version: string;
  token: { name: string; symbol: string; decimals: number };
}

export interface SatchelNetworkResponse {
  esplora: Partial<Record<"regtest" | "signet" | "testnet" | "testnet4" | "mainnet", string>>;
  f1r3fly: {
    validator: F1r3nodeEndpoint;
    observer: F1r3nodeEndpoint;
    /** Null when the validator could not be reached; `status_error` says why. */
    status: ShardStatus | null;
    status_error: string | null;
  };
}

export interface ServerHealthInfo {
  version?: string;
}

export interface ServerHealthResult {
  available: boolean;
  error?: string;
  /** Response latency in milliseconds */
  latency?: number;
  serverInfo?: ServerHealthInfo;
  checkedAt: Date;
}

/** The backend says "wallet"; the product calls it a SATCHEL. */
const rebrandErrorMessage = (message: string): string => {
  return message.replace(/\bWallet\b/g, "SATCHEL").replace(/\bwallet\b/g, "SATCHEL");
};

/**
 * A failed API call. `code` is `CONNECTION_ERROR`, `TIMEOUT`, `UNKNOWN_ERROR`,
 * or `HTTP_<status>` when the server answered with an error.
 */
export class SatchelApiException extends Error {
  constructor(
    public code: string,
    message: string,
    /** HTTP status, when the server answered */
    public status?: number,
  ) {
    super(rebrandErrorMessage(message));
    this.name = "SatchelApiException";
  }
}

export interface SatchelApiConfig {
  /** API base URL. Setting it bypasses the desktop app's main-process bridge. */
  baseUrl: string;
  timeout?: number;
  /**
   * API token, for callers that reach the server directly (tests, scripts).
   * Browser code must not set it: a token in a web bundle is public.
   */
  apiToken?: string;
}

/** When the server last answered a request, of any status. */
let lastReachableAt = 0;

/**
 * Milliseconds since the server last answered anything, or Infinity if it never
 * has. Health polling uses this to distinguish "busy" from "down": a poll can
 * time out while the server is mid-operation, but a server that answered a
 * moment ago is not gone.
 */
export function msSinceServerReachable(): number {
  return lastReachableAt === 0 ? Number.POSITIVE_INFINITY : Date.now() - lastReachableAt;
}

/** An error thrown by fetch or relayed from the main process, as an API failure. */
const transportFailure = (error: unknown): SatchelApiException => {
  const { code, message } = transportError(error);
  return new SatchelApiException(
    code,
    code === "CONNECTION_ERROR" ? "Cannot connect to SATCHEL API server" : message,
  );
};

const walletPath = (network: string, walletName: string): string =>
  `/${encodeURIComponent(network)}/wallet/${encodeURIComponent(walletName)}`;

interface RequestOptions {
  method?: ApiRequest["method"];
  password?: string;
  /** Sent as JSON */
  json?: unknown;
  /** Sent as raw bytes */
  bytes?: ArrayBuffer;
}

export class SatchelApiClient {
  private baseUrl: string;
  private timeout: number;
  private send: Send;

  constructor(config?: Partial<SatchelApiConfig>) {
    const electron = config?.baseUrl ? undefined : getElectronAPI();
    if (electron) {
      this.baseUrl = SATCHEL_API_CONFIG.DEFAULT_API_URL;
      this.send = (request) => electron.satchelApiRequest(request);
    } else {
      this.baseUrl = config?.baseUrl ?? SATCHEL_API_CONFIG.WEB_API_URL;
      this.send = fetchSender(this.baseUrl, config?.apiToken);
    }
    this.timeout = config?.timeout ?? SATCHEL_API_CONFIG.DEFAULT_TIMEOUT;
  }

  private async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let reply: ApiReply;
    try {
      reply = await this.send({
        method: options.method ?? "GET",
        path,
        password: options.password,
        body:
          options.bytes ?? (options.json === undefined ? undefined : JSON.stringify(options.json)),
        timeoutMs: this.timeout,
      });
    } catch (error) {
      throw transportFailure(error);
    }
    lastReachableAt = Date.now();

    if (reply.status < 200 || reply.status >= 300) {
      throw new SatchelApiException(`HTTP_${reply.status}`, replyErrorMessage(reply), reply.status);
    }

    try {
      return JSON.parse(reply.body) as T;
    } catch {
      throw new SatchelApiException("UNKNOWN_ERROR", "The server sent a response that is not JSON");
    }
  }

  // ============================================================
  // Wallet Management
  // ============================================================

  async createWallet(
    name: string,
    password: string,
    network: string = "regtest",
  ): Promise<WalletCreateResponse> {
    return this.request("/wallet/create", { method: "POST", json: { name, password, network } });
  }

  async importWallet(
    name: string,
    mnemonic: string,
    password: string,
    network: string = "regtest",
  ): Promise<WalletImportResponse> {
    return this.request("/wallet/import", {
      method: "POST",
      json: { name, mnemonic, password, network },
    });
  }

  async listWallets(network?: string): Promise<WalletListResponse> {
    const query = network ? `?network=${encodeURIComponent(network)}` : "";
    return this.request(`/wallet/list${query}`);
  }

  /** Permanently removes the wallet's keys and state from the server. */
  async deleteWallet(
    name: string,
    password: string,
    network: string = "regtest",
  ): Promise<WalletDeleteResponse> {
    return this.request(walletPath(network, name), { method: "DELETE", password });
  }

  async getWalletPubkey(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<WalletPubkeyResponse> {
    return this.request(`${walletPath(network, walletName)}/pubkey`, { password });
  }

  // ============================================================
  // Bitcoin Operations
  // ============================================================

  async syncWallet(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<SyncResponse> {
    return this.request(`${walletPath(network, walletName)}/sync`, { method: "POST", password });
  }

  async getBalance(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<BitcoinBalance> {
    return this.request(`${walletPath(network, walletName)}/balance`, { password });
  }

  async getAddresses(
    walletName: string,
    password: string,
    network: string = "regtest",
    count: number = 5,
  ): Promise<WalletAddress[]> {
    return this.request(`${walletPath(network, walletName)}/addresses?count=${count}`, {
      password,
    });
  }

  async listUtxos(
    walletName: string,
    password: string,
    network: string = "regtest",
    options?: {
      availableOnly?: boolean;
      confirmedOnly?: boolean;
      rgbOnly?: boolean;
      minAmountBtc?: number;
    },
  ): Promise<ListUtxosResponse> {
    const params = new URLSearchParams();
    if (options?.availableOnly) params.set("available_only", "true");
    if (options?.confirmedOnly) params.set("confirmed_only", "true");
    if (options?.rgbOnly) params.set("rgb_only", "true");
    if (options?.minAmountBtc !== undefined)
      params.set("min_amount_btc", String(options.minAmountBtc));

    const query = params.toString();
    return this.request(`${walletPath(network, walletName)}/utxos${query ? `?${query}` : ""}`, {
      password,
    });
  }

  /** Create UTXOs on the colored keychain for use as RGB seals. */
  async createColoredUtxos(
    walletName: string,
    password: string,
    network: string = "regtest",
    options?: {
      count?: number;
      size_sats?: number;
      fee_rate_sat_vb?: number;
    },
  ): Promise<CreateColoredUtxosResponse> {
    return this.request(`${walletPath(network, walletName)}/create-colored-utxos`, {
      method: "POST",
      password,
      json: options ?? {},
    });
  }

  async sendBitcoin(
    walletName: string,
    password: string,
    network: string = "regtest",
    toAddress: string,
    amountSats: number,
    feeRateSatVb: number = 2,
  ): Promise<SendBitcoinResponse> {
    return this.request(`${walletPath(network, walletName)}/send-bitcoin`, {
      method: "POST",
      password,
      json: { to_address: toAddress, amount_sats: amountSats, fee_rate_sat_vb: feeRateSatVb },
    });
  }

  // ============================================================
  // RGB Asset Operations
  // ============================================================

  async issueAsset(
    walletName: string,
    password: string,
    network: string = "regtest",
    params: {
      ticker: string;
      name: string;
      supply: number;
      precision: number;
    },
  ): Promise<IssueAssetResponse> {
    return this.request(`${walletPath(network, walletName)}/issue-asset`, {
      method: "POST",
      password,
      json: {
        ticker: params.ticker,
        name: params.name,
        supply: params.supply,
        precision: params.precision,
      },
    });
  }

  async listAssets(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<RGBAsset[]> {
    return this.request(`${walletPath(network, walletName)}/assets`, { password });
  }

  /** Balances of every contract the wallet holds, or of one contract. */
  async getRgbBalance(
    walletName: string,
    password: string,
    network: string = "regtest",
    contractId?: string,
  ): Promise<RGBBalance[]> {
    const query = contractId ? `?contract_id=${encodeURIComponent(contractId)}` : "";
    const response = await this.request<{ balances: RGBBalance[] }>(
      `${walletPath(network, walletName)}/rgb-balance${query}`,
      { password },
    );
    return response.balances;
  }

  /** The genesis consignment a recipient imports before receiving this asset. */
  async exportGenesis(
    walletName: string,
    password: string,
    network: string = "regtest",
    contractId: string,
  ): Promise<Blob> {
    const exported = await this.request<{ contract_id: string; consignment: unknown }>(
      `${walletPath(network, walletName)}/export-genesis/${encodeURIComponent(contractId)}`,
      { password },
    );
    return consignmentBlob(exported.consignment);
  }

  // ============================================================
  // RGB Invoices & Transfers
  // ============================================================

  async generateInvoice(
    walletName: string,
    password: string,
    network: string = "regtest",
    contractId: string,
    amount: number,
  ): Promise<InvoiceResponse> {
    return this.request(`${walletPath(network, walletName)}/generate-invoice`, {
      method: "POST",
      password,
      json: { contract_id: contractId, amount },
    });
  }

  async parseInvoice(invoice: string, network: string = "regtest"): Promise<ParsedInvoice> {
    return this.request("/parse-invoice", { method: "POST", json: { invoice, network } });
  }

  /**
   * Send RGB tokens to an invoice. The server records the transfer before it
   * sends anything, so a call that fails part-way is finished by `refresh`.
   */
  async sendTransfer(
    walletName: string,
    password: string,
    network: string = "regtest",
    invoice: string,
    recipientPubkey: string,
    feeRateSatVb: number = 2,
  ): Promise<SendTransferResponse> {
    return this.request(`${walletPath(network, walletName)}/send-transfer`, {
      method: "POST",
      password,
      json: { invoice, recipient_pubkey: recipientPubkey, fee_rate_sat_vb: feeRateSatVb },
    });
  }

  /** Accept a genesis or transfer consignment file. */
  async acceptConsignment(
    walletName: string,
    password: string,
    network: string = "regtest",
    consignment: ArrayBuffer,
  ): Promise<AcceptConsignmentResponse> {
    return this.request(`${walletPath(network, walletName)}/accept-consignment`, {
      method: "POST",
      password,
      bytes: consignment,
    });
  }

  async listClaims(
    walletName: string,
    password: string,
    network: string = "regtest",
    contractId?: string,
  ): Promise<ListClaimsResponse> {
    const query = contractId ? `?contract_id=${encodeURIComponent(contractId)}` : "";
    return this.request(`${walletPath(network, walletName)}/claims${query}`, { password });
  }

  async listTransfers(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<ListTransfersResponse> {
    return this.request(`${walletPath(network, walletName)}/transfers`, { password });
  }

  /** Advance pending transfers and claims; resumes transfers that were interrupted. */
  async refresh(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<RefreshResponse> {
    return this.request(`${walletPath(network, walletName)}/refresh`, {
      method: "POST",
      password,
    });
  }

  async getVault(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<VaultResponse> {
    return this.request(`${walletPath(network, walletName)}/vault`, { password });
  }

  /** The wallet's recovery phrase; the server releases it only for the wallet's password. */
  async getRecoveryPhrase(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<RecoveryPhraseResponse> {
    return this.request(`${walletPath(network, walletName)}/recovery-phrase`, {
      method: "POST",
      password,
    });
  }

  /**
   * The phrase of the wallet's AluVM (rgb-lib) wallet: derived from the wallet's
   * own phrase with BIP-85, so the one recovery phrase restores both.
   */
  async getAluvmPhrase(
    walletName: string,
    password: string,
    network: string = "regtest",
  ): Promise<RecoveryPhraseResponse> {
    return this.request(`${walletPath(network, walletName)}/aluvm-phrase`, {
      method: "POST",
      password,
    });
  }

  /** The Esplora and F1r3fly endpoints the server uses, and the shard's current state. */
  async getNetwork(): Promise<SatchelNetworkResponse> {
    return this.request("/network");
  }

  // ============================================================
  // Health
  // ============================================================

  /** Kept short: a server that is up answers its health route at once. */
  static readonly HEALTH_CHECK_TIMEOUT = 3000;

  async healthCheck(): Promise<boolean> {
    return (await this.checkServerHealth()).available;
  }

  async checkServerHealth(): Promise<ServerHealthResult> {
    const startTime = Date.now();
    let reply: ApiReply;
    try {
      reply = await this.send({
        method: "GET",
        path: "/health",
        timeoutMs: SatchelApiClient.HEALTH_CHECK_TIMEOUT,
      });
    } catch (error) {
      const failure = transportFailure(error);
      return {
        available: false,
        error:
          failure.code === "TIMEOUT"
            ? "Connection timeout"
            : failure.code === "CONNECTION_ERROR"
              ? "Server not reachable"
              : failure.message,
        latency: Date.now() - startTime,
        checkedAt: new Date(),
      };
    }

    lastReachableAt = Date.now();
    const latency = Date.now() - startTime;
    if (reply.status !== 200) {
      return {
        available: false,
        error: `Server returned HTTP ${reply.status}`,
        latency,
        checkedAt: new Date(),
      };
    }

    let serverInfo: ServerHealthInfo | undefined;
    try {
      const data = JSON.parse(reply.body) as { version?: unknown };
      if (typeof data.version === "string") {
        serverInfo = { version: data.version };
      }
    } catch {
      // A health route that answers 200 without JSON is still up.
    }
    return { available: true, latency, serverInfo, checkedAt: new Date() };
  }

  /** The API address, for display. */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
